/**
 * Dates on a batch's day-wise syllabus.
 *
 * A planned date is a CALENDAR DAY, stored as UTC midnight of that day
 * (vc_be utils/syllabusSchedule). It is read back in UTC, so the day never
 * shifts with the viewer's time zone. `actualDate` — when a day was taught —
 * is an instant, and is read as the IST day it fell on, like the rest of the
 * app's class times.
 */

const DAY_FORMAT = { weekday: "short", day: "numeric", month: "short" };
const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** A stored planned date → 'YYYY-MM-DD'. */
export const dayKey = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

/** 'YYYY-MM-DD' or a stored date → 'Fri, 17 Oct' (with the year when asked). */
export const formatDay = (value, { year = false } = {}) => {
  const key = dayKey(value);
  if (!key) return "—";
  return new Date(`${key}T00:00:00Z`).toLocaleDateString("en-IN", {
    ...DAY_FORMAT,
    ...(year ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
};

/** An instant → the IST day it fell on, 'Fri, 17 Oct'. */
export const formatTaughtDay = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-IN", { ...DAY_FORMAT, timeZone: "Asia/Kolkata" });
};

/** An instant → the IST 'YYYY-MM-DD' it fell on. */
export const taughtDayKey = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() + 330 * 60000).toISOString().slice(0, 10);
};

/** An instant → '16 Oct, 10:42 am' in IST, for the change history. */
export const formatMoment = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });
};

/**
 * The batch's class days in words: [1..6] → 'Mon–Sat', [1,3,5] → 'Mon, Wed, Fri',
 * [0,6] → 'Sat, Sun'. A run of three or more consecutive days is a range.
 */
export const weekdaysLabel = (days) => {
  const list = [...new Set(days || [])].filter((d) => d >= 0 && d <= 6);
  if (!list.length) return "";
  // Monday-first, so a weekend reads "Sat, Sun" rather than "Sun, Sat".
  const order = list.map((d) => (d + 6) % 7).sort((a, b) => a - b);
  const runs = [];
  for (const d of order) {
    const last = runs[runs.length - 1];
    if (last && d === last[1] + 1) last[1] = d;
    else runs.push([d, d]);
  }
  const name = (mondayFirst) => WEEKDAY_NAMES[(mondayFirst + 1) % 7];
  return runs
    .flatMap(([a, b]) => {
      if (b - a >= 2) return [`${name(a)}–${name(b)}`];
      if (b > a) return [name(a), name(b)];
      return [name(a)];
    })
    .join(", ");
};

/** Was this day moved from the plan it was first given? */
export const movedFrom = (session) => {
  const original = dayKey(session?.originalPlannedDate);
  const planned = dayKey(session?.plannedDate);
  return original && planned && original !== planned ? original : null;
};

/** Can this day still be moved? Completed and skipped days keep their date. */
export const isReschedulable = (session) =>
  !!session && session.status !== "completed" && session.status !== "skipped";

/** The sentence for a day sitting on a date the batch does not meet. */
export const conflictText = (conflict) => {
  if (!conflict) return "";
  if (conflict.kind === "holiday") return conflict.title ? `Holiday — ${conflict.title}` : "Holiday";
  return "Not a class day";
};

/** "Class 3" / "Day 3" / "Session 3" — the server's label, else "Day N". */
export const labelOf = (session) => session?.label || `Day ${session?.dayNumber ?? ""}`.trim();

/** "class" / "day" / "session" — the word for one entry of this plan. */
export const nounOf = (session) => (labelOf(session).split(" ")[0] || "Day").toLowerCase();

/** The plan's format word for headings: "classes" / "days" / "sessions". */
export const pluralNoun = (format) => ({ module: "classes", smart: "sessions" }[format] || "days");
